<template>
  <div class="podcast-follow-btn-wrapper">
    <button
      v-if="useLinkVariant"
      class="podcast-follow-btn podcast-follow-btn--link"
      :class="{ 'podcast-follow-btn--followed': view.isFollowed }"
      :disabled="view.isLoading"
      @click="view.toggleFollow()"
      @mouseenter="view.updateLabel($event)"
      @mouseleave="view.updateLabel($event)"
    >
      {{ view.label }}
    </button>

    <AppButton
      v-else
      class="podcast-follow-btn"
      :loading="view.isLoading"
      :primaryAlt="!view.isFollowed"
      :outlined="view.isFollowed"
      @click="view.toggleFollow()"
      @mouseenter="view.updateLabel($event)"
      @mouseleave="view.updateLabel($event)"
    >
      <div class="podcast-follow-btn__label">
        {{ view.label }}
      </div>
      <div class="podcast-follow-btn__placeholder">Following</div>
    </AppButton>

    <Transition name="fade">
      <div v-if="view.hint" class="podcast-follow-btn__hint">
        <p class="podcast-follow-btn__hint-text">{{ view.hint }}</p>
        <button class="podcast-follow-btn__close-hint" @click="view.hideHint()">
          <span class="iconfont iclose"></span>
        </button>
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import type { PodcastFollowView } from '@/models/docs.list.podcasts.episodes'
import AppButton from '@/app/components/button/AppButton.vue'

withDefaults(
  defineProps<{
    view: PodcastFollowView
    useLinkVariant?: boolean
  }>(),
  {
    useLinkVariant: false,
  },
)
</script>

<style lang="scss" scoped>
.podcast-follow-btn-wrapper {
  position: relative;
  width: fit-content;
}

.podcast-follow-btn {
  display: flex;

  &__placeholder {
    // This placeholder element prevents the button from resizing
    // when the label changes
    visibility: hidden;
  }

  &__label {
    position: absolute;
  }

  &__close-hint {
    display: none;

    @media only screen and (max-width: $w-tablet-s) {
      display: block;
      font-size: 12px;
      width: 22px;
      height: 22px;
      margin-bottom: -2px;
      color: $c-black-3;
    }
  }

  &__hint-text {
    color: $c-black-2;
    text-align: center;
    margin-bottom: -2px;

    @media only screen and (max-width: $w-tablet-s) {
      text-align: left;
    }
  }

  &__hint {
    // On desktop, it's displayed horizontally centered
    // above the follow button.
    --follow-btn-width: 390px;
    position: absolute;
    z-index: 1;
    bottom: calc(100% + 12px);
    left: calc(50% - (var(--follow-btn-width) / 2));
    width: var(--follow-btn-width);
    border-radius: 8px;
    padding: 16px 24px;
    background: $c-white;
    box-shadow: $shadow;

    @media only screen and (max-width: $w-largest) {
      // On smaller desktops, centering it horizontally
      // makes it overflow the viewport, so we align it
      // with the follow button
      left: 0;
    }

    @media only screen and (max-width: $w-tablet-s) {
      // On mobile, it's displayed at the top of the viewport
      position: fixed;
      top: var(--container-padding);
      left: var(--container-padding);
      z-index: 6;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 8px;
      width: calc(100% - (var(--container-padding) * 2));
      height: fit-content;
    }
  }
}

.podcast-follow-btn--link {
  color: $c-blue-4;
  font-weight: 400;
  @include hover {
    color: $c-blue-2;
  }

  &.podcast-follow-btn--followed {
    color: $c-black-3;
    @include hover {
      color: $c-black-2;
    }
  }

  &:disabled {
    cursor: unset;
    opacity: 0.3;
  }
}

.night-mode .podcast-follow-btn {
  &__hint {
    background: $c-dark-1;
  }

  &__hint-text {
    color: $c-dark-4;
  }
}

.night-mode .podcast-follow-btn--link {
  &.podcast-follow-btn--followed {
    @include hover {
      color: $c-blue-4;
    }
  }
}
</style>
